/**
 * Bank Sync API Endpoints
 * Open Banking integration (Bridge / Powens)
 */

import apiClient from '../client';
import logger from '../../utils/logger';

// Types
export interface DataProvider {
  id: string;
  name: string;
  display_name: string;
  provider_type: 'aggregator' | 'manual' | 'import';
  country_codes?: string[];
  capabilities: {
    accounts: boolean;
    transactions: boolean;
    investments: boolean;
  };
}

export type ConnectionStatus =
  | 'pending'
  | 'connected'
  | 'syncing'
  | 'error'
  | 'disconnected'
  | 'sca_needed'
  | 'webauth_needed'
  | 'expired';

export interface DataProviderConnection {
  id: string;
  status: ConnectionStatus;
  external_connection_id: string | null;
  institution_name: string | null;
  institution_logo_url: string | null;
  masked_account_number: string | null;
  display_name: string;
  last_synced_at: string | null;
  last_sync_error_at: string | null;
  last_sync_error_message: string | null;
  consecutive_failures: number;
  sync_config: Record<string, any>;
  created_at: string;
  updated_at: string;
  provider: DataProvider | null;
}

export interface InitiateConnectionResponse {
  success: boolean;
  redirect_url: string;
  connection_id: string;
  provider: string;
}

export interface CallbackResponse {
  id: string;
  status: ConnectionStatus;
  provider: DataProvider;
}

export interface ReconnectResponse {
  success: boolean;
  redirect_url: string;
}

export interface SyncResponse {
  success: boolean;
  status: 'sync_queued';
}

export interface DisconnectResponse {
  success: boolean;
}

export type SyncLogStatus = 'started' | 'in_progress' | 'completed' | 'failed' | 'partial';

export interface SyncLog {
  id: string;
  sync_type: string;
  status: SyncLogStatus;
  started_at: string;
  completed_at: string | null;
  accounts_synced: number;
  transactions_created: number;
  transactions_updated: number;
  transactions_skipped: number;
  duration_ms: number | null;
  error_message: string | null;
}

export interface FeatureStatusResponse {
  enabled: boolean;
  coming_soon: boolean;
  feature_name: string;
  description: string;
  estimated_launch: string | null;
  benefits: string[];
}

export interface ComingSoonError {
  error: 'feature_coming_soon';
  coming_soon: true;
  message: string;
  feature_name: string;
  estimated_launch: string;
}

export interface PremiumRequiredError {
  error: 'premium_required';
  message: string;
}

export interface TokenExpiredError {
  error: 'token_expired';
  message: string;
  action: 'initiate_new_connection';
}

// API Endpoints
export const bankSyncApi = {
  /**
   * GET /data_provider_connections/status
   * Check if bank connection feature is enabled (or coming soon)
   */
  getFeatureStatus: async (): Promise<FeatureStatusResponse> => {
    logger.log('[BankSync] Checking feature status');
    const response = await apiClient.get<FeatureStatusResponse>(
      '/data_provider_connections/status'
    );
    return response.data;
  },

  /**
   * GET /data_provider_connections/providers
   * List available bank providers (aggregators)
   */
  listProviders: async (): Promise<DataProvider[]> => {
    logger.log('[BankSync] Fetching providers');
    const response = await apiClient.get<DataProvider[]>(
      '/data_provider_connections/providers'
    );
    return response.data;
  },

  /**
   * GET /data_provider_connections
   * List user's bank connections
   */
  listConnections: async (): Promise<DataProviderConnection[]> => {
    logger.log('[BankSync] Fetching connections');
    const response = await apiClient.get<DataProviderConnection[]>(
      '/data_provider_connections'
    );
    return response.data;
  },

  /**
   * POST /data_provider_connections/initiate
   * Start bank connection OAuth flow (Premium only)
   * Provider is determined server-side via BANK_SYNC_PROVIDER env var
   * @returns Redirect URL to open in browser
   */
  initiateConnection: async (): Promise<InitiateConnectionResponse> => {
    logger.log('[BankSync] Initiating connection');
    const response = await apiClient.post<InitiateConnectionResponse>(
      '/data_provider_connections/initiate',
      {}
    );
    return response.data;
  },

  /**
   * POST /data_provider_connections/:id/callback
   * Complete OAuth callback after user returns from bank
   * @param connectionId - Wealfi connection ID from initiate
   * @param externalId - Powens connection_id or Bridge item_id from callback redirect
   */
  completeCallback: async (
    connectionId: string,
    externalId: string
  ): Promise<CallbackResponse> => {
    logger.log('[BankSync] Completing callback for connection:', connectionId, 'external:', externalId);
    const response = await apiClient.post<CallbackResponse>(
      `/data_provider_connections/${connectionId}/callback`,
      { connection_id: externalId, item_id: externalId }
    );
    return response.data;
  },

  /**
   * POST /data_provider_connections/:id/reconnect
   * Get reconnect URL for SCA re-authentication (Premium only)
   * @param connectionId - Connection ID
   * @returns Reconnect URL to open in WebView
   */
  getReconnectUrl: async (
    connectionId: string
  ): Promise<ReconnectResponse> => {
    logger.log('[BankSync] Getting reconnect URL for connection:', connectionId);
    const response = await apiClient.post<ReconnectResponse>(
      `/data_provider_connections/${connectionId}/reconnect`
    );
    return response.data;
  },

  /**
   * POST /data_provider_connections/:id/sync
   * Trigger manual sync (Premium only)
   * @param connectionId - Connection ID
   */
  triggerSync: async (connectionId: string): Promise<SyncResponse> => {
    logger.log('[BankSync] Triggering sync for connection:', connectionId);
    const response = await apiClient.post<SyncResponse>(
      `/data_provider_connections/${connectionId}/sync`
    );
    return response.data;
  },

  /**
   * GET /data_provider_connections/:id/sync_logs
   * Fetch sync history for a connection
   * @param connectionId - Connection ID
   * @param limit - Max number of logs to return (default 20)
   */
  getSyncLogs: async (connectionId: string, limit: number = 20): Promise<SyncLog[]> => {
    logger.log('[BankSync] Fetching sync logs for connection:', connectionId);
    const response = await apiClient.get<SyncLog[]>(
      `/data_provider_connections/${connectionId}/sync_logs`,
      { params: { limit } }
    );
    return response.data;
  },

  /**
   * PATCH /data_provider_connections/:id
   * Update connection display name
   * @param connectionId - Connection ID
   * @param displayName - New display name
   */
  updateDisplayName: async (connectionId: string, displayName: string): Promise<DataProviderConnection> => {
    logger.log('[BankSync] Updating display name for connection:', connectionId);
    const response = await apiClient.patch<DataProviderConnection>(
      `/data_provider_connections/${connectionId}`,
      { display_name: displayName }
    );
    return response.data;
  },

  /**
   * DELETE /data_provider_connections/:id
   * Disconnect a bank connection
   * @param connectionId - Connection ID
   */
  disconnect: async (connectionId: string): Promise<DisconnectResponse> => {
    logger.log('[BankSync] Disconnecting connection:', connectionId);
    const response = await apiClient.delete<DisconnectResponse>(
      `/data_provider_connections/${connectionId}`
    );
    return response.data;
  },

  /**
   * Check if an error is a premium_required error
   */
  isPremiumRequiredError: (error: any): error is { response: { data: PremiumRequiredError } } => {
    return error?.response?.data?.error === 'premium_required';
  },

  /**
   * Check if an error is a token_expired error requiring new connection
   */
  isTokenExpiredError: (error: any): error is { response: { data: TokenExpiredError } } => {
    return error?.response?.data?.error === 'token_expired' &&
           error?.response?.data?.action === 'initiate_new_connection';
  },

  /**
   * Check if an error is a coming_soon feature error
   */
  isComingSoonError: (error: any): error is { response: { data: ComingSoonError } } => {
    return error?.response?.data?.error === 'feature_coming_soon' &&
           error?.response?.data?.coming_soon === true;
  },
};

export default bankSyncApi;
